import React, { useEffect, useRef } from "react";
import { Provider } from "react-redux";
import store from "./redux/store";
import { HashRouter as Router } from "react-router-dom";
import Views from "./views";
import { Route, Switch } from "react-router-dom";
import { onSwitchTheme } from "./redux/actions/Theme";
import OneSignalManager from "./components/OneSignalManager";
import PresenceService from "./services/PresenceService";
import { iniciarAtualizacaoVersao } from "./utils/atualizacaoVersao";

const DARK_READER_CONFIG = {
	brightness: 100,
	contrast: 90,
	sepia: 10,
};

const DARK_READER_FIXES = {
	invert: [],
	css: "",
	ignoreInlineStyle: [".semana-btn-cor"],
	ignoreImageAnalysis: [],
	disableStyleSheetsProxy: false,
};

function applyTheme(currentTheme) {
	const isDark = currentTheme === "dark";
	document.body.classList.toggle("dark", isDark);
	if (window.DarkReader) {
		if (isDark) {
			window.DarkReader.enable(DARK_READER_CONFIG, DARK_READER_FIXES);
		} else {
			window.DarkReader.disable();
		}
	}
}

function App() {
	const oneSignalRef = useRef();

	// Tema: recuperar do localStorage e reagir a mudanças via store.subscribe
	useEffect(() => {
		const savedTheme = localStorage.getItem("app-theme");
		if (savedTheme && (savedTheme === "light" || savedTheme === "dark")) {
			store.dispatch(onSwitchTheme(savedTheme));
		}

		applyTheme(store.getState().theme.currentTheme);

		const unsubscribe = store.subscribe(() => {
			applyTheme(store.getState().theme.currentTheme);
		});

		return () => unsubscribe();
	}, []);

	// Monitorar mudanças no token de autenticação
	useEffect(() => {
		const handleStorageChange = (e) => {
			if (e.key === "token") {
				if (e.newValue) {
					// Usuário fez login
					if (oneSignalRef.current) {
						oneSignalRef.current.onUserLogin();
					}
				} else {
					// Usuário fez logout
					if (oneSignalRef.current) {
						oneSignalRef.current.onUserLogout();
					}
				}
			}
		};

		window.addEventListener("storage", handleStorageChange);

		// Verificar token inicial
		const token = localStorage.getItem("token");
		if (token && oneSignalRef.current) {
			oneSignalRef.current.onUserLogin();
		}

		return () => {
			window.removeEventListener("storage", handleStorageChange);
		};
	}, []);

	// Heartbeat de presença (web): POST /v1/rpc/presenca enquanto a aba está visível
	useEffect(() => {
		const stop = PresenceService.start();
		return () => stop();
	}, []);

	// Detecção proativa de deploy novo: recarrega silenciosamente na próxima
	// navegação (hashchange) quando o asset-manifest aponta uma versão diferente
	// da que está carregada. Nunca recarrega fora da navegação.
	useEffect(() => {
		const parar = iniciarAtualizacaoVersao();
		return () => parar();
	}, []);

	return (
		<div className="App">
			<Provider store={store}>
				<OneSignalManager ref={oneSignalRef}>
					<Router>
						<Switch>
							<Route path="/" component={Views} />
						</Switch>
					</Router>
				</OneSignalManager>
			</Provider>
		</div>
	);
}

export default App;
